import { MeasuringSystem } from "../SessionModel"
import { LmLocation } from "../../../models"

/** API `distanceToPin` is feet (matches FS app). Do not use generic convertDistance FEET (yards×3). */
const FT_TO_M = 0.3048

export const convertProximityFeetForDisplay = (
    feet: number,
    measuringSystem: MeasuringSystem
): number =>
    measuringSystem === MeasuringSystem.metric ? feet * FT_TO_M : feet

enum DistanceUnit {
    METERS = "METERS",
    FEET = "FEET"
}
const convertDistance = (value: number, distanceUnit?: DistanceUnit) => {
    switch (distanceUnit) {
        case DistanceUnit.METERS:
            return value * 0.9144
        case DistanceUnit.FEET:
            return value * 3
    }
    return value
}

/**
 * Label for a drill target distance.
 *
 * These are nominal preset labels, not measured distances — the API ids are literally
 * `..._target_60y` / `_target_70y` / `_target_80y`. The Golf App keeps the number and
 * only swaps the unit suffix, so a 60 target reads "60y" in imperial and "60m" in
 * metric. Converting yards → metres here rendered it as "55m" — KWSA-34.
 *
 * This matches how the dispersion rings are already labelled in generateGrids.ts,
 * where 50 becomes "50y" or "50m" without conversion.
 */
export const formatTargetDistance = (distance: number | null | undefined, measuringSystem: MeasuringSystem): string => {
    if (distance === 0 || distance == null || !Number.isFinite(distance)) return "Tee";
    return measuringSystem === MeasuringSystem.metric ? `${distance}m` : `${distance}y`;
}

/**
 * Canonical drill target name, always in yards.
 *
 * The Combine Summary radar is labelled with the target's name rather than a
 * unit-aware distance: the target is defined in yards (API ids are `..._target_60y`)
 * and the Golf App keeps that label in yards even while the session is shown in
 * metric — so the radar reads "60y / 70y / 80y" next to a "60m" Best Target — KWSA-34.
 */
export const formatDrillTargetName = (distance: number | null | undefined): string => {
    if (distance === 0 || distance == null || !Number.isFinite(distance)) return "Tee";
    return `${distance}y`;
}

// const convertElevation = (value: number, elevationUnit?: DistanceUnit.FEET) => {
//     switch (elevationUnit) {
//         case DistanceUnit.FEET:
//             return value * 3.28084
//     }
//     return value
// }

enum TemperatureUnit {
    F = "F",
    C = "C"
}
const convertTemprature = (value: number, temperatureUnit?: TemperatureUnit) => {
    switch (temperatureUnit) {
        case TemperatureUnit.F:
            return (value * 9 / 5) + 32;
        // case TemperatureUnit.C:
        //     return ((value - 32) * 5) / 9;
    }
    return value;
}

enum SpeedUnit {
    KPH = "KPH",
    MPS = "MPS"
}
const convertSpeed = (value: number, speedUnit?: SpeedUnit) => {
    switch (speedUnit) {
        case SpeedUnit.KPH:
            return value * 1.60934
        case SpeedUnit.MPS:
            return value / 2.237
    }
    return value
}

export const UnitConversions = (key: string, shot: any, isNormalized: boolean, measuringSystem: MeasuringSystem, location?: string) => {
    let valueString: string = "";
    let side: string = "";
    let sideValueString: string = "---";
    let value: number = shot[key];
    let orgValue: number = shot[key];

    // Metrics that carry a separate normalized value from the API. When the raw
    // reading is invalid but a normalized value exists, the normalized branches
    // below still produce a real value — so in normalized mode we must not
    // short-circuit to "---" (which would also drop the shot from the average).
    //
    // "side" is intentionally excluded in every location: Side Carry follows the raw
    // sideValid flag even in normalized mode, so an invalid side reading shows "---"
    // (matching the Golf App) instead of the fabricated normalized side — KWSA-30.
    //
    // "apex" is also excluded in every location. KWSA-31 removed it because an invalid
    // raw apex falls back to a normalized value that is not physically plausible
    // (observed: 4.2 ft for an 8-iron carrying 111 yd, 3.3 ft for a gap wedge), which
    // then corrupts AVG Apex. No ticket asks for normalized Apex to be surfaced.
    //
    // "carryDistance" depends on the session location, because the Golf App treats an
    // invalid raw reading differently per mode — both behaviours were reported against
    // identical shot data (raw null, carryDistanceValid false, a normalized value present):
    //   - Simulator ("Sim Mode"): show "---" and drop the shot from the average, so the
    //     normalized value is never surfaced — KWSA-31.
    //   - Any other location (Screen, Net, Indoor, Outdoor): fall back to the normalized
    //     value, e.g. Carry "295" rather than "---" — KWSA-33.
    const isSimulator = location === LmLocation.SIMULATOR;
    const NORMALIZED_KEYS = isSimulator
        ? ["totalDistance", "sideTotal", "distanceToPin"]
        : ["totalDistance", "sideTotal", "distanceToPin", "carryDistance"];
    const hasNormalizedValue =
        isNormalized &&
        NORMALIZED_KEYS.includes(key) &&
        shot.normalizedValues != null &&
        shot.normalizedValues[key] != null;

    // Face to Path is derived (faceAngle - clubPath) and has no own *Valid flag,
    // so it is exempt from the generic guard below. It is only valid when both
    // components are valid — otherwise return "---" so it matches the FS app and
    // is excluded from the average (which drops shots whose valueString is "---").
    if (key === "faceToPath" && (!shot.faceAngleValid || !shot.clubPathValid)) {
        return {
            valueString: "---",
            value: 0,
            side: "",
            sideValueString: "---"
        }
    }

    if (key !== "faceToPath" && (!shot[`${key}Valid`] && !["distanceToPin"].includes(key)) && !hasNormalizedValue) {
        return {
            valueString: "---",
            value: 0,
            side: "",
            sideValueString: "---"
        }
    }

    value = !isNaN(value) ? value : 0
    let measuringUnit = getMeasuringUnit(key, measuringSystem);

    // KWSA-38: Total, Apex, Side Carry and Side Total display to one decimal place.
    if (["totalDistance"].includes(key)) {
        if (isNormalized) {
            value = (shot.normalizedValues && shot.normalizedValues[key] && convertDistance(shot.normalizedValues[key], measuringUnit)) || 0
            valueString = (shot.normalizedValues && shot.normalizedValues[key] && value.toFixed(1).toString()) || "---";
        } else {
            value = convertDistance(value, measuringUnit);
            valueString = value.toFixed(1).toString();
        }
    }
    if (["carryDistance"].includes(key)) {
        if (isNormalized) {
            value = (shot.normalizedValues && shot.normalizedValues[key] && convertDistance(shot.normalizedValues[key], measuringUnit)) || 0
            valueString = (shot.normalizedValues && shot.normalizedValues[key] && value.toFixed(1).toString()) || "---";
        } else {
            value = convertDistance(value, measuringUnit);
            valueString = value.toFixed(1).toString();
        }
    }
    if (["apex"].includes(key)) {
        if (isNormalized) {
            value = (shot.normalizedValues?.apex && convertDistance(shot.normalizedValues?.apex, measuringUnit)) || 0
            valueString = (shot.normalizedValues?.apex && value.toFixed(1).toString()) || "---";
        } else {
            value = convertDistance(value, measuringUnit);
            valueString = value.toFixed(1).toString();
        }
    }
    if (["distanceToPin"].includes(key)) {
        if (isNormalized) {
            const rawFt = shot.normalizedValues?.[key]
            if (rawFt !== undefined && rawFt !== null && !Number.isNaN(Number(rawFt))) {
                value = convertProximityFeetForDisplay(Number(rawFt), measuringSystem)
                valueString = value.toFixed().toString()
            } else {
                value = 0
                valueString = "---"
            }
        } else if (orgValue == null) {
            value = 0
            valueString = "---"
        } else {
            value = convertProximityFeetForDisplay(Number(orgValue), measuringSystem)
            valueString = value.toFixed(1).toString()
        }
    }

    if (["spinRate"].includes(key)) {
        valueString = value.toFixed().toString();
    }

    if (["faceToPath"].includes(key)) {
        valueString = `${(shot.faceAngle - shot.clubPath).toFixed(1).toString()}°`;
        value = shot.faceAngle - shot.clubPath;
    }

    if (["smashFactor"].includes(key)) {
        valueString = value.toFixed(2).toString();
    }

    if (["ballSpeed", "clubSpeed"].includes(key)) {
        value = convertSpeed(value, measuringUnit);
        valueString = value.toFixed(1).toString();
    }

    // KWSA-42: descentAngle joins the plain-degree metrics — one decimal, no uom.
    if (["launchAngle", "descentAngle", "clubPath", "faceAngle", "attackAngle", "spinAxis", "horizontalLaunchAngle"].includes(key)) {
        valueString = `${value.toFixed(1).toString()}°`;
    }

    if (["side", "sideTotal"].includes(key)) {
        if (isNormalized) {
            // Use a null check (not truthiness) so a legitimate side of 0 (a dead-straight
            // shot) is a real value rather than being dropped to "---" — KWSA-30.
            const normalizedSide = shot.normalizedValues != null ? shot.normalizedValues[key] : null;
            if (normalizedSide != null) {
                value = convertDistance(normalizedSide, measuringUnit)
                if (value === 0) {
                    side = ""
                }
                if (value > 0) {
                    side = "R"
                }
                if (value < 0) {
                    side = "L"
                }
                valueString = `${Math.abs(value).toFixed(1).toString()}`;
                sideValueString = value.toFixed(1).toString();
            } else {
                value = 0;
                valueString = "---";
                sideValueString = "---";
            }
        } else {
            value = convertDistance(value, measuringUnit);
            if (value === 0) {
                side = ""
            }
            if (value > 0) {
                side = "R"
            }
            if (value < 0) {
                side = "L"
            }
            valueString = `${Math.abs(value).toFixed(1).toString()}`;
            sideValueString = value.toFixed(1).toString();
        }
    }

    return {
        valueString,
        value,
        side,
        sideValueString
    }
}

export const getTemparature = (value: number, measuringSystem: MeasuringSystem) => {
    if (value) {
        let measuringUnit = getMeasuringUnit("temperature", measuringSystem)
        let temperatureConverted = convertTemprature(value, measuringUnit)
        return `${temperatureConverted.toFixed()}° ${measuringUnit}`
    }
    return "N/A";
}


export const getElevation = (value: number, measuringSystem: MeasuringSystem) => {
    if (value != null) {
        return `${value.toFixed()} Feet`;
    }
    return "N/A";
}

export const getMeasuringUnit = (key: string, measuringSystem: MeasuringSystem) => {
    let keys: any = {
        carryDistance: {
            imperial: "YARDS",
            metric: "METERS"
        },
        totalDistance: {
            imperial: "YARDS",
            metric: "METERS"
        },
        distanceToPin: {
            imperial: "FEET",
            metric: "METERS"
        },
        apex: {
            imperial: "FEET",
            metric: "METERS"
        },
        ballSpeed: {
            imperial: "MPH",
            metric: "KPH"
        },
        clubSpeed: {
            imperial: "MPH",
            metric: "KPH"
        },
        side: {
            imperial: "YARDS",
            metric: "METERS"
        },
        sideTotal: {
            imperial: "YARDS",
            metric: "METERS"
        },
        temperature: {
            imperial: "F",
            metric: "C"
        },
        spinRate: {
            imperial: "RPM",
            metric: "RPM"
        }
    }
    return (keys[key] && keys[key][measuringSystem]) || "";
}
export const formatNumber = (value: number, decimals: number = 0): string => {
    if (value == null || isNaN(value)) return "0";
    return Number(value.toFixed(decimals)).toLocaleString();
};
export const randomNumber = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
};